import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * Focus the page heading (h1) after every route change (not on the first load). When that is the heading of a loading
 * page, the focus follows to the heading that replaces it – unless the user has moved the focus elsewhere meanwhile.
 */
export function useRouteFocus(): void {
  const location = useLocation();
  // the location the focus was last handled for; the first load keeps the browser's focus (skip link first).
  // Comparing locations (not a "first run" flag) also survives StrictMode's double effect in development.
  const handled = useRef(location.pathname + JSON.stringify(location.state ?? null));
  useEffect(() => {
    const current = location.pathname + JSON.stringify(location.state ?? null);
    if (handled.current === current) return;
    handled.current = current;
    const state = location.state as { focus?: string } | null;
    let frame = 0;
    let attempts = 0;
    let observer: MutationObserver | null = null;
    let timeout = 0;
    const focus = (heading: HTMLElement) => {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus();
      // the heading of LoadingPage: the page's own heading replaces it once the data is there
      if (heading.hasAttribute('data-loading-heading')) follow(heading);
    };
    const follow = (loading: HTMLElement) => {
      const main = loading.closest('main') ?? document.body;
      observer?.disconnect();
      observer = new MutationObserver(() => {
        const next = loading.isConnected ? null : main.querySelector<HTMLElement>('h1');
        if (!next) return;
        observer?.disconnect();
        // only when the focus went down with the loading heading
        if (!document.activeElement || document.activeElement === document.body) focus(next);
      });
      observer.observe(main, { childList: true, subtree: true });
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => observer?.disconnect(), 30_000);
    };
    const tryFocus = () => {
      const explicit = state?.focus ? document.getElementById(state.focus) : null;
      const heading = explicit ?? document.querySelector<HTMLElement>('main h1');
      if (heading) return focus(heading);
      if (attempts++ < 60) frame = requestAnimationFrame(tryFocus);
    };
    frame = requestAnimationFrame(tryFocus);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.clearTimeout(timeout);
    };
  }, [location.pathname, location.state]);
}
